import type { RouteLocationRaw } from 'vue-router'
import { withBetaOptIn } from '@/util/beta_opt_in'
import { toolkitUrls } from '@/util/links'
import { FROM_HOME } from './event_names'
import type { CardHeader, CardLink, CardOtherLink } from './instructional_design_card_types'

export type InstructionalDesignCardConfig = {
  key: string
  accentColor: string
  hoverColor: string
  imgSrc: string
  header: CardHeader
  cardDescription: string
  button: CardLink
  otherLinks: CardOtherLink[]
}

export type InstructionalDesignAudience = {
  isIodVisible: boolean
  isEffectsVisible: boolean
  isAiFeatureVisible: boolean
  isBetaKept: boolean
}

const PRESENT_LEVELS_HEADER: CardHeader = { line1: 'Identify', line2: 'Present Levels' }
const EFFECT_STATEMENTS_HEADER: CardHeader = { line1: 'Identify Present Levels &', line2: 'Effect Statements' }

// Pages still behind ?beta=true fall back to Vue 2 when a link drops the opt-in, so Home carries it through.
function homeHref(url: string, isBetaKept: boolean): string {
  const link = new URL(url)
  link.searchParams.set('ref', FROM_HOME.ref)
  if (!isBetaKept) {
    return link.toString()
  }
  return withBetaOptIn(link.toString())
}

function homeRoute(name: string, isBetaKept: boolean): RouteLocationRaw {
  if (!isBetaKept) {
    return { name, query: FROM_HOME }
  }
  return { name, query: { ...FROM_HOME, beta: 'true' } }
}

function isNewPresentLevelsWizard({ isIodVisible, isEffectsVisible, isAiFeatureVisible }: InstructionalDesignAudience): boolean {
  const hasPresentLevelsStatements = isIodVisible || isEffectsVisible
  return hasPresentLevelsStatements && isAiFeatureVisible
}

function presentLevelsWizardUrl(audience: InstructionalDesignAudience): string {
  if (isNewPresentLevelsWizard(audience)) {
    return toolkitUrls.presentlevelsWizardV2
  }
  return toolkitUrls.presentlevelsWizard
}

function presentLevelsImage(audience: InstructionalDesignAudience): string {
  if (isNewPresentLevelsWizard(audience)) {
    return toolkitUrls.homepageAssets.newPresentLevelsImg
  }
  return toolkitUrls.homepageAssets.presentLevelsImg
}

function presentLevelsHeader({ isEffectsVisible }: InstructionalDesignAudience): CardHeader {
  if (isEffectsVisible) {
    return EFFECT_STATEMENTS_HEADER
  }
  return PRESENT_LEVELS_HEADER
}

function presentLevelsStatementKind({ isEffectsVisible }: InstructionalDesignAudience): string {
  if (isEffectsVisible) {
    return 'effect'
  }
  return 'impact'
}

function presentLevelsOtherLinks(audience: InstructionalDesignAudience): CardOtherLink[] {
  const { isIodVisible, isEffectsVisible, isAiFeatureVisible, isBetaKept } = audience
  const browsePresentLevels = { text: 'Browse present levels', href: homeHref(toolkitUrls.browsePresentLevels, isBetaKept) }
  const composeImpact = {
    text: 'Compose impact of disability',
    href: homeHref(toolkitUrls.impactOfDisability(isAiFeatureVisible, isBetaKept), isBetaKept),
    isHiddenWithoutIod: true
  }
  const browseAssessments = { text: 'Browse assessments', href: homeHref(toolkitUrls.browseAssessments, isBetaKept) }

  if (isIodVisible && isAiFeatureVisible) {
    return [browseAssessments, composeImpact]
  }
  // Effect statement profiles (e.g. Nevada) use the new wizard and no longer offer Browse Present Levels.
  if (isEffectsVisible) {
    return [composeImpact, browseAssessments]
  }
  return [browsePresentLevels, composeImpact, browseAssessments]
}

function presentLevelsCard(audience: InstructionalDesignAudience): InstructionalDesignCardConfig {
  return {
    key: 'present-levels',
    accentColor: '#733a95',
    hoverColor: '#4E1470',
    imgSrc: presentLevelsImage(audience),
    header: presentLevelsHeader(audience),
    cardDescription: `Find baseline assessments and write meaningful, data-driven present levels with strengths, needs, and ${presentLevelsStatementKind(audience)} statements.`,
    button: { text: 'Present Levels Wizard', href: homeHref(presentLevelsWizardUrl(audience), audience.isBetaKept) },
    otherLinks: presentLevelsOtherLinks(audience)
  }
}

function goalsCard({ isBetaKept }: InstructionalDesignAudience): InstructionalDesignCardConfig {
  return {
    key: 'goals',
    accentColor: '#00769f',
    hoverColor: '#04506A',
    imgSrc: toolkitUrls.homepageAssets.goalsImg,
    header: { line1: 'Develop', line2: 'Goals & Assessments' },
    cardDescription: 'Write measurable, standard-aligned goals based on student need, and find resources to monitor progress toward goals.',
    button: { text: 'Learning Goal Wizard', href: homeHref(toolkitUrls.goalWizard, isBetaKept) },
    otherLinks: [
      { text: 'Browse goals', href: homeHref(toolkitUrls.browse, isBetaKept) },
      { text: 'View goal index', to: homeRoute('GoalIndexPage', isBetaKept) },
      { text: 'Browse assessments', href: homeHref(toolkitUrls.browseAssessments, isBetaKept) }
    ]
  }
}

function strategiesCard({ isBetaKept }: InstructionalDesignAudience): InstructionalDesignCardConfig {
  return {
    key: 'strategies',
    accentColor: '#136320',
    hoverColor: '#064612',
    imgSrc: toolkitUrls.homepageAssets.strategiesImg,
    header: { line1: 'Discover', line2: 'Strategies & Supports' },
    cardDescription: 'Implement IEPs with research-based strategies and accommodations for delivering specially designed instruction.',
    button: { text: 'Strategy Wizard', to: homeRoute('StrategyWizard', isBetaKept) },
    otherLinks: [
      { text: 'Browse strategies', to: homeRoute('BrowseStrategiesPage', isBetaKept) },
      { text: 'View strategy index', to: homeRoute('StrategyIndex', isBetaKept) }
    ]
  }
}

export function instructionalDesignCards(audience: InstructionalDesignAudience): InstructionalDesignCardConfig[] {
  return [presentLevelsCard(audience), goalsCard(audience), strategiesCard(audience)]
}
